/**
 * SKU管理主页面
 * 职责: 组织SKU管理的各个组件
 * 限制: <150行
 */

import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { Table, Form, Layout } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/Layout/PageHeader';
import { EmptyState } from '../../components/UI/EmptyState';
import { useSKUManagement } from './useSKUManagement';
import { createSKUTableColumns } from './SKUTableColumns';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useSKUStats } from './hooks/useSKUStats';
import { SKUStats } from './components/SKUStats';
import { SKUDrawer } from './components/SKUDrawer';
import { CategorySidebar } from './components/CategorySidebar';
import './SKUManagement.css';

const { Sider, Content } = Layout;

export const SKUManagement: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const {
    skus,
    loading,
    modalVisible,
    brands,
    brandMode,
    editingSKU,
    setBrandMode,
    setModalVisible,
    loadSKUs,
    loadBrands,
    handleCreate,
    handleDelete,
    handleSubmit,
  } = useSKUManagement();

  const stats = useSKUStats(skus);

  // Filter SKUs by Category
  const filteredSkus = useMemo(() => {
    if (!selectedCategory) return skus;
    return skus.filter(sku => sku.category && sku.category.startsWith(selectedCategory));
  }, [skus, selectedCategory]);

  // 自动保存功能 - 仅在编辑模式下启用
  const { triggerSave, saveNow, state: autoSaveState } = useAutoSave({
    onSave: useCallback(async (formData: any) => {
      if (!editingSKU) return;
      const { skuAPI } = await import('../../services/api');
      return await skuAPI.update(editingSKU.id, formData);
    }, [editingSKU]),
    delay: 2000,
    enabled: !!editingSKU,
    showMessage: true,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSKUs();
    loadBrands();
  }, []);

  const onDrawerSubmit = async () => {
    try {
      const values = await form.validateFields();
      const success = await handleSubmit(values);
      if (success) {
        form.resetFields();
      }
    } catch (error) {
      // 表单验证失败
    }
  };

  const onDrawerClose = () => {
    setModalVisible(false);
    form.resetFields();
  };

  const columns = createSKUTableColumns({
    onEdit: (sku) => navigate(`/sku-management/edit/${sku.id}`),
    onDelete: handleDelete,
    onViewDetail: (id: number) => navigate(`/skus/${id}`),
  });

  return (
    <div className="sku-management-page">
      <PageHeader
        title="SKU管理"
        subtitle="管理您的产品SKU信息"
        actions={[
          {
            key: 'create',
            label: '新建SKU',
            type: 'primary',
            icon: <PlusOutlined />,
            onClick: handleCreate,
          },
        ]}
      />

      <Layout style={{ flex: 1, background: 'transparent', overflow: 'hidden' }}>
        <Sider width={280} theme="light" style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(10px)', marginTop: 24, marginLeft: 24, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '16px', height: '100%' }}>
            <CategorySidebar onSelect={setSelectedCategory} />
          </div>
        </Sider>
        <Content style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="sku-management-content">
            <SKUStats stats={stats} loading={loading} />

            {skus.length === 0 && !loading ? (
              <EmptyState
                type="no-data"
                title="还没有SKU"
                description="创建第一个SKU开始您的分析之旅"
                action={{
                  label: '新建SKU',
                  onClick: handleCreate,
                }}
              />
            ) : (
              <div className="sku-table-card">
                <Table
                  columns={columns}
                  dataSource={Array.isArray(filteredSkus) ? filteredSkus : []}
                  loading={loading}
                  rowKey="id"
                  scroll={{ x: 1200 }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                  }}
                />
              </div>
            )}
          </div>
        </Content>
      </Layout>

      <SKUDrawer
        visible={modalVisible}
        editingSKU={editingSKU}
        brands={brands}
        brandMode={brandMode}
        autoSaveState={autoSaveState}
        onClose={onDrawerClose}
        onSubmit={onDrawerSubmit}
        onSaveNow={saveNow}
        onBrandModeChange={setBrandMode}
        onValuesChange={(_, allValues) => {
          if (editingSKU) triggerSave(allValues);
        }}
        form={form}
      />
    </div>
  );
};

export default SKUManagement;
